import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { appStateActions } from 'store/appStateSlice';
import { adminActions } from 'features/admin/store/slice';
import {
  useMountEffect,
  useAssets,
  usePerformActionOnSuccess,
  useEditMode,
} from 'shared/hooks';
import { SuccessActionTypesEnum } from 'store/SuccessActionTypes.enum';
import { isFile } from 'utils/helpers';
import { UserRoleEnum } from 'utils/enums';

const useUserEdit = () => {
  const dispatch = useDispatch();
  const { userId } = useParams();
  const { user } = useSelector(({ admin }) => admin);
  const editMode = useEditMode();
  const { assetUrl, fetchAssets, uploadAsset } = useAssets();

  const fetchUser = useCallback(() => {
    dispatch(adminActions.getUserById(userId));
  }, [dispatch, userId]);

  usePerformActionOnSuccess(SuccessActionTypesEnum.USER_UPDATED, () => {
    dispatch(appStateActions.hideSnackbar());
    fetchUser();
    editMode.disableEditMode();
  });

  useMountEffect(() => {
    fetchAssets([userId]);
  });

  const onUserUpdate = async ({ userImage, ...data }) => {
    if (isFile(userImage)) {
      try {
        await uploadAsset(userId, userImage);
      } catch (_e) {
        return;
      }
    }
    dispatch(
      adminActions.updateUser({
        id: userId,
        ...data,
      }),
    );
  };

  const userData = useMemo(
    () =>
      userId === user?.id
        ? {
            ...user,
            userImage: user?.userImage ?? assetUrl(userId),
          }
        : {},
    [userId, user, assetUrl],
  );

  const isProvider = useMemo(
    () =>
      Array.isArray(userData?.roles) &&
      userData.roles.some(
        (role) =>
          role !== UserRoleEnum.CUSTOMER && role !== UserRoleEnum.ADMINISTRATOR,
      ),
    [userData?.roles],
  );
  return {
    userId,
    userData,
    isProvider,
    fetchUser,
    onUserUpdate,
    ...editMode,
  };
};

export default useUserEdit;
